//import { locateProxy } from '#~/agent/index.js';
import { useAutoTrackingControlProtection } from '#~/auto-tracking/control-protection/hooks.js';
import { Command } from '#~/command/index.js';
import { toEffectiveTool } from '#~/control/effective-tool.js';
import { useToolPref } from '#~/control/hooks.js';
import { Tool } from '#~/control/index.js';
import { useTemplate } from '#~/device/registry/hooks.js';
import { useHotKeys } from '#~/hot-keys/hooks.js';
import { equalTo, toCombo } from '#~/hot-keys/index.js';
import { PostHogFeatureFlag, useFeatureFlag } from '#~/posthog/index.js';
import { useCalibration, useRoom } from '#~/room/hooks.js';
import { Handler as AutoTrackingHandler } from './autotracking/handler.jsx';
import { Handler as CenterHandler } from './center/handler.jsx';
import { Handler as FastFrameHandler } from './fast-frame/handler.jsx';
import { Handler as JoystickHandler } from './joystick/handler.jsx';
import { Joystick } from './joystick/joystick.jsx';
import type { HandlerProps, Props } from './types.js';
import { isPrimaryPointer } from './utils.js';
import { Handler as ZoomHandler } from './zoom/handler.jsx';
import type { Coordinate } from '@studio/js-utils/geometry';
import { isCalibrated, isCalibrating } from '@studio/models/control/calibration';
import { useDocumentActive, useLatestFn, useThrottledFn, χref } from '@studio/react-utils/hooks';
import { isNotNil } from '@tryiris-ai/js-types';
import { noop } from 'lodash-es';
import type {
  ComponentPropsWithRef,
  ComponentType,
  KeyboardEvent,
  PointerEvent,
  ReactElement
} from 'react';
import { createElement, useEffect, useMemo, useRef, useState } from 'react';
import { twMerge } from 'cn';

const Handlers: Record<Tool, ComponentType<ComponentPropsWithRef<'div'> & HandlerProps>> = {
  [Tool.Center]: CenterHandler,
  [Tool.FastFrame]: FastFrameHandler,
  [Tool.Joystick]: JoystickHandler,
  [Tool.Tracking]: () => null,
  [Tool.Zoom]: ZoomHandler
};

/** `<Interaction>` component. */
export const Interaction = ({ className, deviceId, ...props }: Props): ReactElement => {
  const [override, setOverride] = useState<{
    element: ReactElement;
    originalTool: Tool;
  }>();

  const [isMouseInside, setMouseInside] = useState(false);
  const isDocumentActive = useDocumentActive();
  const selfRef = useRef<HTMLDivElement>(null);
  const positionRef = useRef<Coordinate | undefined>(undefined);
  const [tool, setTool] = useToolPref();
  const hotKeys = useHotKeys();
  //const proxy = locateProxy(deviceId);
  const calibration = useCalibration(deviceId);

  // ENG-4495 C4/D4: resolve the device's driver-identity bit so the activation-path guard can block
  // the magic tools on a driverless (generic) model. The top-tray button-disable (C4) does not reach
  // the keyboard-shortcut / dropdown activation paths, so the gate must be enforced here too.
  // D3: tri-state, permissive on absence — only a LOADED generic === true blocks (never `?? false`).
  const modelId = useRoom((µ) => µ.devices[deviceId]?.modelId);
  const template = useTemplate(modelId);
  const bGeneric = template?.model?.generic === true;

  // ENG-4495 D5: derive the EFFECTIVE tool so a generic device whose persisted selection is a
  // permanently-blocked magic tool (default is Tool.Center) falls back to the Joystick manual
  // backstop — click/drag then MOVES the camera instead of being inert. This is a derived value,
  // NOT a pref write (useToolPref stays untouched), so returning to a branded camera restores the
  // user's real choice. Non-generic devices are returned unchanged (AC4).
  const effectiveTool = toEffectiveTool(tool, bGeneric);

  const { enabled: enableAutotracking } = useFeatureFlag(PostHogFeatureFlag.AUTOTRACKING, {
    defaultValue: false
  });

  const handler = useMemo(() => {
    // https://iris-ai.atlassian.net/browse/CON-867
    //
    // ENG-4495 C4/D4 + D5: a driverless (generic) model blocks the driver-dependent tools —
    // Center / FastFrame / Tracking — regardless of calibration state. The D5 effective-tool fallback
    // already remaps a generic magic selection to Joystick (so `effectiveTool` is never a magic tool
    // on a generic device); the `bGeneric ||` term is kept as defense-in-depth for the C4/D4
    // activation-path backstop (shortcut / dropdown), which the top-tray button-disable never reached.
    if (
      isCalibrating(calibration) ||
      ((!isCalibrated(calibration) || bGeneric) &&
        [Tool.Center, Tool.FastFrame, Tool.Tracking].includes(effectiveTool))
    ) {
      return undefined;
    }

    if (effectiveTool === Tool.Tracking && !enableAutotracking) {
      return undefined;
    }

    return Handlers[effectiveTool];
  }, [bGeneric, calibration, effectiveTool, enableAutotracking]);

  const clearOverride = useLatestFn(() => {
    // Restore original tool.
    setOverride((value) => {
      if (value) {
        queueMicrotask(() => {
          setTool(value.originalTool);
        });
      }

      return undefined;
    });
  });

  // Watch for document activity changes.
  useEffect(() => {
    if (!isDocumentActive) {
      setMouseInside(false);
    }
  }, [isDocumentActive]);

  // Manage focus and keyboard listeners.
  useEffect(() => {
    const self = χref(selfRef);

    // Is the mouse inside this component?
    if (!isMouseInside || !self) {
      clearOverride();

      return noop;
    }

    // Keep track of the last focused element.
    const lastActive = document.activeElement;

    // Take focus!
    self.focus();

    return () => {
      // Restore focus to the last focused element.
      if (lastActive instanceof HTMLElement) {
        lastActive.focus();
      }
    };
  }, [isMouseInside]);

  const { isProtected, triggerAlert: triggerControlAlert } =
    useAutoTrackingControlProtection(deviceId) ?? {};

  // Handle keyboard events.
  const handleKeyboardEvent = useThrottledFn((event: KeyboardEvent) => {
    const trigger = hotKeys[Command.TOOL_JOYSTICK_TRIGGER];
    const combo = toCombo(event);

    // Ignore everything except non-repeating `TOOL_JOYSTICK_TRIGGER` key events.
    if (event.repeat || !trigger || !combo || !equalTo(combo, trigger)) {
      return;
    }

    // Whenever the `Space` key is pressed, we temporarily engage the joystick tool.
    if (event.type === 'keydown') {
      const origin = χref(positionRef);
      const self = χref(selfRef);

      // Is the pointer really over this component?
      if (origin && self?.contains(document.elementFromPoint(origin.x, origin.y))) {
        if (isProtected) {
          triggerControlAlert?.();

          return;
        }

        setOverride({
          element: <Joystick deviceId={deviceId} origin={origin} />,
          originalTool: tool
        });

        queueMicrotask(() => {
          setTool(Tool.Joystick);
        });

        return;
      }

      // We must have missed the 'pointerleave' event.
      setMouseInside(false);
    }

    clearOverride();
  }, 250);

  // Handle mouse events.
  const handlePointerEvent = useLatestFn((event: PointerEvent) => {
    if (!isPrimaryPointer(event)) {
      return;
    }

    if (event.type === 'pointerleave') {
      setMouseInside(false);

      return;
    }

    setMouseInside(true);

    // Keep track of the current pointer position.
    positionRef.current = { x: event.clientX, y: event.clientY };
  });

  const Element = useMemo(
    () =>
      override?.element ??
      (isNotNil(handler)
        ? createElement(handler, {
            className: 'absolute inset-0 right-0 left-0 bottom-0 top-0 z-30',
            deviceId
          })
        : null),
    [deviceId, handler, override]
  );

  return (
    <div
      className={twMerge('select-none overflow-hidden', className)}
      key={`interaction_container_${deviceId}`}
      onBlur={(): void => {
        setMouseInside(false);
      }}
      onKeyDown={handleKeyboardEvent}
      onKeyUp={handleKeyboardEvent}
      onPointerEnter={handlePointerEvent}
      onPointerLeave={handlePointerEvent}
      onPointerMove={handlePointerEvent}
      ref={selfRef}
      role='presentation'
      tabIndex={-1}
      {...props}
    >
      <div className='absolute bottom-0 left-0 right-0 top-0'>
        {/*
          ENG-4495 C4/D4: the auto-tracking overlay is a SECOND Tracking activation path, separate
          from `handler`/`Element` above. The tool preference persists across device selection, so a
          leftover `Tracking` selection would engage Auto-Tracking on a driverless (generic) model —
          gate it here too so a generic device never runs the magic tool.
        */}
        {enableAutotracking && tool === Tool.Tracking && !bGeneric ? (
          <AutoTrackingHandler
            key={`interaction_container_${deviceId}_at_handler`}
            focused={tool === Tool.Tracking}
            className='absolute inset-0 bottom-0 left-0 right-0 top-0 z-30'
            deviceId={deviceId}
          />
        ) : null}
        {Element}
      </div>
    </div>
  );
};
